/**
 * The redux-first-router routes map for the Redact app (this app does not use react-router).
 *
 * Each entry maps a route action type from @common-modules/routing to a URL path, a
 * code-split page component under src/redact-app/pages, a requiresAuth flag, and an
 * optional init saga run on navigation (e.g. initMainPage, initMediaDetailsPage,
 * initDocumentEditor). Both editor routes set a confirmLeave guard that
 * prompts before leaving with unsaved changes, except on the unstoppable exits
 * (isUnstoppableExit). They differ on switching files: the document editor
 * lets a document switch through (VE-29142), the media editor prompts on it
 * (VE-30855). The matched entry's component is
 * lazy-rendered by src/common-app/pages/RootRouter/index.tsx.
 *
 * Which saga an entry names is load-bearing beyond "what runs here". The routing saga
 * keeps a route saga running across a navigation when BOTH routes set
 * `preserveSagaOnRouteChange` and name the same saga function (VE-26729). Eight entries
 * opt in — Home, Settings Profile, Redaction Codes, Statuses, Lexicons,
 * Watermarks, Ingest,
 * Ingest/GovQA and Trash — all naming initMainPage on purpose, so switching between them
 * restarts nothing.
 * Giving one of them its own saga, or dropping its flag, silently makes every switch to
 * and from it a cold re-init again. `test/redact-app/unit/pages/routes.test.ts` pins that
 * invariant.
 *
 * Those same entries, plus the Case Dashboard and the URL-only /admin pages (the landing,
 * /admin/app-config and /admin/dependencies), also set `showsAppBar`, which tells RootRouter
 * to render the shared aiWARE header above the page. It owns the single instance so the bar
 * is not unmounted and remounted on every switch (VE-26729); the editor routes leave the
 * flag off because they render their own header. The /admin pages set `showsAppBar` without
 * `preserveSagaOnRouteChange` — each names its own saga (or none), and the flag only
 * preserves between routes naming the same saga function.
 */
import {
  Action,
  NOT_FOUND,
  ReceivedAction,
  RoutesMap,
  StateGetter,
} from 'redux-first-router';
import { initCasesList } from '@cbsa-modules/mainPage/sagas';
import {
  MEDIA_LEAVE_ELSEWHERE_SIGNAL,
  MEDIA_LEAVE_TO_HOME_SIGNAL,
  MediaLeaveSignal,
  selectTdoIsChanged,
} from '@common-modules/mediaDetails';
import {
  initMainPage,
  initMediaDetailsPage,
} from '@redact-state/modules/mainPage/sagas';
import {
  ROUTE_FORBIDDEN,
  ROUTE_FILE_IN_TRASH,
  ROUTE_HOME,
  ROUTE_REDACTION_CODES,
  ROUTE_WATERMARKS,
  ROUTE_LOAD_FAILED,
  ROUTE_LOGOUT,
  ROUTE_MEDIA_DETAILS,
  ROUTE_INGEST,
  ROUTE_INGEST_GOVQA,
  ROUTE_REDACT_CASEDASHBOARD,
  ROUTE_ADMIN,
  ROUTE_ADMIN_APP_CONFIG,
  ROUTE_LEXICONS,
  ROUTE_REDACTION_STATUSES,
  ROUTE_SETTINGS_PROFILE,
  ROUTE_TRASH,
  ROUTE_KEYBOARD_SHORTCUTS,
  ROUTE_DOCUMENT_DETAILS,
  ROUTE_ADMIN_DEPENDENCIES,
} from '@common-modules/routing';
import { initDocumentEditor } from '@redact-modules/documents/sagas/routeSaga';
import { initAdminPage } from '@redact-modules/admin/sagas';
import {
  selectIsAdminAccessKnownDenied,
  selectIsAppConfigAccessKnownDenied,
} from '@redact-modules/admin/selectors';
import { initAdminDependenciesPage } from '@redact-modules/adminDependencies/sagas';
import { selectHasEditorLeaveRisk } from '@redact-modules/documents/selectors';
import {
  selectFeatureFlags,
  selectIsKnownWithoutDocumentRedaction,
} from '@common/user-permissions';

// Shared importer references for modules reachable from more than one route. RootRouter
// caches its React.lazy wrappers per importer, so distinct arrow functions for the same
// module would each get their own wrapper — and a wrapper that has not resolved yet
// suspends, flashing the full-page loading screen on a navigation between them (VE-26729).
const importMainPage = () => import(`@redact-pages/MainPage`);
const importNotFound = () => import(`@common-pages/NotFound`);

export type Routes = RoutesMap<{
  component: () => Promise<any>;
  requiresAuth?: boolean;
  /**
   * Opt in to keeping this route's `saga` running when navigating BETWEEN routes that
   * both set this flag and name the same saga function (VE-26729).
   *
   * Opt-in, not inferred from saga identity alone: the editor routes also share a saga
   * with themselves, and they re-enter their own route type with a different payload
   * (a case-less deep link injects the caseId and redirects, trim redirects to the new
   * TDO, "view redacted file" redirects to another tdoId). Those re-entries MUST re-run
   * the route saga, so they must never be preserved.
   */
  preserveSagaOnRouteChange?: boolean;
  /**
   * Render the shared aiWARE app bar (components/AppBar) above this route's page.
   *
   * RootRouter owns the single instance so it survives navigation between routes that
   * both set this (VE-26729) — the pages used to each render their own, and the
   * unmount/remount on every switch was a visible flash of the header. Routes with their
   * own header (the media and document editors) leave it off; mounting both would stack
   * two bars.
   */
  showsAppBar?: boolean;
  redirects?: {
    test: (
      getState: StateGetter<any>,
      action: Action | ReceivedAction
    ) => boolean;
    to: Action;
  }[];
  saga?: () => Generator<any, void, unknown>;
}>;

/**
 * Destinations neither editor's leave guard may block.
 *
 * Each is somewhere the app sends the reviewer because their session or their
 * permissions have already failed — refusing the navigation does not give them
 * their work back, it just strands them somewhere that cannot save it.
 */
const UNSTOPPABLE_EXITS = [ROUTE_LOGOUT, ROUTE_FORBIDDEN, ROUTE_LOAD_FAILED];

/**
 * What both editor guards check — not UNSTOPPABLE_EXITS alone.
 *
 * RFR does not hand confirmLeave the dispatched action: it rebuilds one from
 * the destination PATH (createConfirm → pathToAction), taking the first route
 * whose path matches. ROUTE_LOAD_FAILED shares '/not-found' with NOT_FOUND,
 * which is listed first, so inside a guard a load failure always arrives as
 * NOT_FOUND and `ROUTE_LOAD_FAILED.match()` never fires (VE-30855). Matching
 * NOT_FOUND is what actually lets it through.
 *
 * Giving ROUTE_LOAD_FAILED its own path would be cleaner, but mainPage's saga
 * keys off `prevRoute.pathname === '/not-found'`. The cost of this way: Back or
 * Forward onto a URL no route matches also passes unprompted.
 */
const isUnstoppableExit = (action: Action) =>
  action.type === NOT_FOUND ||
  UNSTOPPABLE_EXITS.some((route) => route.match(action));

/**
 * The admin TOOLS are in the instance root organization, at TWO different bars
 * (VE-30689 — the root org alone let any of its users in):
 *
 * - app-config edits the definitions every tenant inherits from the root org,
 *   so SUPERUSER only — Redact policy, not a platform rule (see the header of
 *   `@redact-modules/admin/selectors`);
 * - dependencies only reports registry health, so any admin right will do.
 *
 * `/admin` itself is deliberately NOT guarded — see its entry below.
 *
 * Both guards catch the WARM path only. On the first route of a page load
 * `onBeforeChange` runs before boot, so neither the user record (the rights)
 * nor the root-org check (fetched by the pages) has answered yet; the
 * `KnownDenied` selectors let that through rather than reading "not loaded" as
 * "no rights", which sent every real admin Home on a reload. Both tool pages
 * therefore carry the strict test and redirect once the answers land,
 * AdminAppConfig withholds the definitions scan until then, and the
 * /admin/dependencies route saga checks rights before it runs anything.
 *
 * The root-org half fails open on `unknown`: the cluster did not answer, and
 * failing closed there would take the page from the people it is for on
 * exactly the deployments that cannot prove they are entitled to it. The
 * rights half, once loaded, fails closed — see `selectIsSuperuser`.
 */
const appConfigRequiresSuperuser = {
  test: (getState: StateGetter<any>) =>
    selectIsAppConfigAccessKnownDenied(getState() as never),
  to: ROUTE_HOME(),
};

const adminToolRequiresAdminRights = {
  test: (getState: StateGetter<any>) =>
    selectIsAdminAccessKnownDenied(getState() as never),
  to: ROUTE_HOME(),
};

const routes: Routes = {
  [ROUTE_LOGOUT.type]: {
    path: '/logout',
    component: () => import(`@redact-pages/Logout`),
    requiresAuth: false,
  },
  [ROUTE_HOME.type]: {
    path: '/',
    component: importMainPage,
    requiresAuth: true,
    saga: initMainPage,
    preserveSagaOnRouteChange: true,
    showsAppBar: true,
  },
  [ROUTE_SETTINGS_PROFILE.type]: {
    path: '/settings-profile',
    component: () => import('@redact-pages/SettingsProfile'),
    requiresAuth: true,
    saga: initMainPage,
    preserveSagaOnRouteChange: true,
    showsAppBar: true,
  },
  [ROUTE_KEYBOARD_SHORTCUTS.type]: {
    path: '/keyboard-shortcuts',
    component: () => import('@redact-pages/KeyboardShortcuts'),
  },
  [ROUTE_REDACTION_CODES.type]: {
    path: '/redaction-codes',
    component: () => import('@redact-pages/RedactionCodes'),
    requiresAuth: true,
    saga: initMainPage,
    preserveSagaOnRouteChange: true,
    showsAppBar: true,
    // The `redactionCodes` flag belongs HERE and on the MenuDrawer entry — the two places
    // that are actually about redaction codes. It used to gate the app bar's burger
    // instead, which removed the whole side nav (Statuses, Profiles, Trash) while leaving
    // this route reachable by URL: exactly inverted.
    //
    // Fails OPEN on a cold deep link: the flag's org-application-config layer is async and
    // usually lands after onBeforeChange runs, so an org that disables it this way can
    // still reach the page by typing the URL directly. Deliberate — bouncing a user who
    // IS entitled to the page is the worse error, and the menu entry covers the normal
    // path. The `redactionCodesRegistryId` half of the flag is synchronous and does gate
    // this from the first navigation.
    redirects: [
      {
        test: (getState) => !selectFeatureFlags(getState()).redactionCodes,
        to: ROUTE_HOME(),
      },
    ],
  },
  [ROUTE_WATERMARKS.type]: {
    path: '/watermarks',
    component: () => import('@redact-pages/Watermarks'),
    requiresAuth: true,
    saga: initMainPage,
    // Both flags, like every other initMainPage settings page. Without them a
    // switch to or from Watermarks cancels initMainPage and re-runs it cold —
    // refetching the media list, the org tasks/jobs queries and the folder
    // tree — and RootRouter unmounts the shared header, which is exactly the
    // cost VE-26729 removed. Keep `routes.test.ts`'s two whitelists in step.
    preserveSagaOnRouteChange: true,
    showsAppBar: true,
    // Same fail-open shape as Redaction Codes above, and for the same reason:
    // the flag's org-application-config layer lands after onBeforeChange on a
    // cold deep link, so this gates the warm path and the menu entry covers
    // the rest. Without it the kill switch hides the nav item but leaves
    // /watermarks reachable by typing the URL. The document-redaction half
    // goes through `selectIsKnownWithoutDocumentRedaction` so that it fails open
    // the same way (VE-29497).
    redirects: [
      {
        // Both flags. Watermarks only ever reach a PDF, so on an org without
        // document redaction this page configures something that cannot be
        // used — and `selectFeatureFlags` already merges the org application
        // config in for `enableDocumentRedaction`, so this is the same source
        // the document editor itself gates on. MenuDrawer applies the same
        // pair; if the two drift, the kill switch hides the nav item and
        // leaves /watermarks reachable by typing the URL.
        test: (getState) =>
          !selectFeatureFlags(getState()).enableWatermarks ||
          selectIsKnownWithoutDocumentRedaction(getState()),
        to: ROUTE_HOME(),
      },
    ],
  },
  [ROUTE_ADMIN.type]: {
    path: '/admin',
    component: () => import('@redact-pages/Admin'),
    requiresAuth: true,
    // NOT guarded, deliberately — it is where a non-admin user or a non-root
    // org finds out why the tools are closed to them. The cards render locked, with the reason on
    // hover; bouncing them off this page too would leave the whole area
    // answering a question nobody could ask.
    //
    // No saga: the root-org check it dispatches is watched app-wide from the
    // root saga, because the side nav asks the same question from every page.
    showsAppBar: true,
  },
  [ROUTE_ADMIN_APP_CONFIG.type]: {
    path: '/admin/app-config',
    component: () => import('@redact-pages/AdminAppConfig'),
    requiresAuth: true,
    // Its own saga, so deliberately NOT preserveSagaOnRouteChange: that flag
    // only preserves between routes naming the SAME saga function, and the
    // settings pages all name initMainPage. Setting it here would preserve
    // nothing and quietly break `routes.test.ts`'s opted-in whitelist.
    saga: initAdminPage,
    // The shared app bar, like every other full-page route — this page has no
    // header of its own. It is not in the side menu (the ticket puts a nav
    // entry out of scope), so the bar is the only chrome it gets.
    showsAppBar: true,
    redirects: [appConfigRequiresSuperuser],
  },
  [ROUTE_REDACTION_STATUSES.type]: {
    path: '/redaction-statuses',
    component: () => import('@redact-pages/RedactionStatuses'),
    requiresAuth: true,
    // VE-26729: deliberately the SAME saga as the other opted-in routes, so switching
    // between them tears nothing down. initMainPage forks the statuses admin watchers
    // this page needs; the page fetches the statuses themselves on mount.
    saga: initMainPage,
    preserveSagaOnRouteChange: true,
    showsAppBar: true,
  },
  [ROUTE_LEXICONS.type]: {
    path: '/lexicons',
    component: () => import('@redact-pages/Lexicons'),
    requiresAuth: true,
    // VE-24948: joins the preserve-saga group (VE-26729) — same saga identity as
    // the other settings-like routes, so switching among them restarts nothing.
    // initMainPage forks the lexicon admin watchers; the page fetches the list
    // itself on mount (a preserved saga's start-up effects don't re-fire on
    // re-entry — the Statuses page precedent).
    saga: initMainPage,
    preserveSagaOnRouteChange: true,
    showsAppBar: true,
    // Gated on document redaction, and on that ALONE. A lexicon only ever
    // applies to a PDF, so an org without document redaction has no use for
    // this page; `MenuDrawer` hides the entry on the same flag, and the two
    // have to agree or the switch leaves /lexicons reachable by typing it.
    //
    // There is deliberately NO registry gate here. That was tried twice — first
    // `window.config.lexiconRegistryId`, then a `customLexicon` flag — and both
    // bounced the user to Home for reasons unrelated to whether the feature
    // worked, since that config comes from a gitignored `static/config.js` when
    // standalone and is replaced wholesale by `window.aiwareConfig` under the
    // aiWARE shell. An environment whose registry cannot be resolved surfaces
    // at runtime as `isNotConfigured`, and the page renders an explanatory
    // panel — a better answer to a deep link than a silent redirect.
    //
    // Fails open on a cold deep link until the org config has loaded; see
    // `selectIsKnownWithoutDocumentRedaction` (VE-29497).
    redirects: [
      {
        test: (getState) => selectIsKnownWithoutDocumentRedaction(getState()),
        to: ROUTE_HOME(),
      },
    ],
  },
  [ROUTE_INGEST.type]: {
    path: '/ingest',
    component: importMainPage,
    requiresAuth: true,
    saga: initMainPage,
    preserveSagaOnRouteChange: true,
    showsAppBar: true,
  },
  [ROUTE_INGEST_GOVQA.type]: {
    path: '/ingest/GovQA/:govQARequestId',
    component: importMainPage,
    requiresAuth: true,
    saga: initMainPage,
    preserveSagaOnRouteChange: true,
    showsAppBar: true,
  },
  // route_media_details
  [ROUTE_MEDIA_DETAILS.type]: {
    path: '/(case)?/:caseId?/files/:tdoId?',
    component: () => import(`@redact-pages/MediaDetails`),
    requiresAuth: true,
    // TODO Move into actual sagas
    saga: initMediaDetailsPage,
    /**
     * VE-30855 — every exit with unsaved edits asks first, not just Home.
     *
     * Opening another file (case drawer row, row-menu Redact, review modal
     * "Open in editor", a job notification, Back/Forward) re-enters this route,
     * and committing that wipes the file: the reducer resets the slice to
     * defaultState and watchGoToMediaDetailsPage clears the worker store.
     * Unlike the document editor, nothing is suspended — the edits are simply
     * gone — so a switch must prompt here even though the document editor's
     * switch deliberately does not.
     *
     * There is no same-file exemption, on purpose. The reset above does not
     * check the tdoId, so re-entering THIS file on another path (/files/X vs
     * /case/C/files/X, via Back/Forward or a caseId-less notification) wipes it
     * just the same. The UI entry points no-op on the open file instead.
     *
     * RFR holds the action until the modal answers, so Cancel leaves the
     * current file untouched. That only holds if nothing resets state BEFORE
     * the route action — the drawer used to, see RESET_MEDIA_DETAILS_ON_SWITCH.
     *
     * The returned constant is a signal, never displayed (the modal renders
     * its own i18n copy): the save modal's Discard runs the Home-exit extras
     * only for MEDIA_LEAVE_TO_HOME_SIGNAL (see leaveSignals.ts).
     */
    // redux-first-router ConfirmLeave type is just bad
    confirmLeave: (state: any, action: any): MediaLeaveSignal | undefined => {
      if (!selectTdoIsChanged(state)) {
        return undefined;
      }
      // Same reasoning as the document editor: a redirect to logout, forbidden
      // or load-failed cannot be refused without stranding the reviewer.
      if (isUnstoppableExit(action)) {
        return undefined;
      }
      return ROUTE_HOME.match(action)
        ? MEDIA_LEAVE_TO_HOME_SIGNAL
        : MEDIA_LEAVE_ELSEWHERE_SIGNAL;
    },
  },
  [ROUTE_TRASH.type]: {
    path: '/trash',
    component: () => import(`@redact-pages/Trash`),
    requiresAuth: true,
    saga: initMainPage,
    preserveSagaOnRouteChange: true,
    showsAppBar: true,
  },
  // URL-only (no nav entry): the runbook's data-registry smoke check. Its own saga and no
  // preserveSagaOnRouteChange, so every visit re-runs the checks against the live API.
  [ROUTE_ADMIN_DEPENDENCIES.type]: {
    path: '/admin/dependencies',
    component: () => import('@redact-pages/AdminDependencies'),
    requiresAuth: true,
    saga: initAdminDependenciesPage,
    showsAppBar: true,
    // The LOOSER guard — this page writes nothing, so an Organization Admin is
    // welcome where app-config needs a superuser. The landing page locks this
    // tool's card on the same test, and a lock the URL bar walks around is a
    // suggestion.
    redirects: [adminToolRequiresAdminRights],
  },
  [ROUTE_FILE_IN_TRASH.type]: {
    path: '/file-in-trash/:tdoId?',
    component: () => import(`@redact-pages/FileInTrash`),
    requiresAuth: true,
  },
  [NOT_FOUND]: {
    path: '/not-found',
    component: importNotFound,
    requiresAuth: true,
  },
  [ROUTE_LOAD_FAILED.type]: {
    path: '/not-found',
    component: importNotFound,
    requiresAuth: true,
  },
  [ROUTE_FORBIDDEN.type]: {
    path: '/forbidden',
    component: () => import(`@redact-pages/Forbidden`),
    requiresAuth: true,
  },
  [ROUTE_REDACT_CASEDASHBOARD.type]: {
    path: '/redact2/casedashboard',
    component: () => import('@redact-pages/CaseDashboard'),
    requiresAuth: true,
    saga: initCasesList,
    showsAppBar: true,
  },
  [ROUTE_DOCUMENT_DETAILS.type]: {
    path: '/(case)?/:caseId?/documents/:tdoId',
    component: () => import('@redact-pages/DocumentDetails'),
    requiresAuth: true,
    saga: initDocumentEditor,
    /**
     * VE-29142 — the BACK BUTTON, and every other in-app exit.
     *
     * `beforeunload` covers closing the tab and leaving the app; it does not
     * fire on a redux-first-router route change, so until this existed the
     * back button discarded unsaved redactions in silence. The router's own
     * confirm is the only hook that sees those, and it is already wired
     * app-wide (`displayConfirmLeave` in configureStore → the
     * BLOCK_NAVIGATION_MODAL state the editor renders from).
     *
     * SWITCHING DOCUMENTS MUST NOT PROMPT. A switch dispatches this same route
     * type with a different tdoId, so a guard that only asked "is there unsaved
     * work" would fire on every switch — reinstating the save decision this
     * ticket exists to remove, and on the one action that does not lose
     * anything (the outgoing document is suspended, not discarded). Hence the
     * same-type bail, which is the first thing to check if switching ever
     * starts prompting again.
     *
     * The media editor's guard is equally broad but DOES prompt on a switch
     * (VE-30855): its outgoing file is discarded, not suspended.
     *
     * The string is a fallback. The editor renders its own modal off this
     * state, because the message that matters here names the affected files
     * and the router's API carries only a line of text.
     */
    // redux-first-router ConfirmLeave type is just bad
    confirmLeave: (state: any, action: any) => {
      if (ROUTE_DOCUMENT_DETAILS.match(action)) {
        return undefined;
      }
      // Exits that are not the reviewer's to refuse. RFR runs confirmLeave for
      // `redirect()` actions like any other, and the app redirects to LOGOUT on
      // token expiry (`common-app/state/modules/app/sagas`) and to FORBIDDEN on
      // a permission failure (`common-app/state/modules/routing/sagas`).
      // Prompting there offers a "Stay" that discards the redirect and leaves
      // the reviewer in an unauthenticated app whose every call will fail —
      // work they cannot save, on a screen that looks normal. This is the
      // price of going broad; the media editor's guard pays it too.
      if (isUnstoppableExit(action)) {
        return undefined;
      }
      return selectHasEditorLeaveRisk(state as never)
        ? 'You have work in progress in this document editor.'
        : undefined;
    },
  },
};

export default routes;
